import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { getBanners, bannerImageUrl } from "../api/banner";
import { getNotices } from "../api/notice";

/**
 * 스크롤에 따라 배너 이미지가 본문보다 느리게 움직이는 패럴랙스 오프셋(px)을 계산한다.
 * factor 가 클수록, 스크롤 초반(배너가 화면에서 사라지기 시작하는 시점)에 빠르게 최대
 * 이동폭까지 도달해 효과가 뚜렷하게 느껴진다. 최대 이동폭은 이미지 실제 높이의 비율
 * (maxRatio) 로 계산해 화면 크기와 무관하게 항상 안전한 범위 안에서만 움직인다.
 * (이미지는 CSS 에서 scale(1.32) 로 확대되어 있어 약 16%의 여유 공간이 있다.)
 * 사용자가 "동작 줄이기"(prefers-reduced-motion) 를 설정했다면 효과를 적용하지 않는다.
 */
function useParallaxOffset(elementRef, factor = 0.5, maxRatio = 0.15) {
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return undefined;
    }

    let ticking = false;
    const applyOffset = () => {
      const height = elementRef.current?.offsetHeight || 260;
      const max = height * maxRatio;
      setOffset(Math.min(Math.max(window.scrollY * factor, -max), max));
      ticking = false;
    };
    const onScrollOrResize = () => {
      if (!ticking) {
        window.requestAnimationFrame(applyOffset);
        ticking = true;
      }
    };

    applyOffset();
    window.addEventListener("scroll", onScrollOrResize, { passive: true });
    window.addEventListener("resize", onScrollOrResize, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScrollOrResize);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [elementRef, factor, maxRatio]);

  return offset;
}

function BannerSlide({ banner, parallaxOffset }) {
  const content = (
    <div className="hero-banner-slide-content">
      <img
        className="hero-banner-image"
        style={{ transform: `scale(1.32) translateY(${parallaxOffset}px)` }}
        src={bannerImageUrl(banner.bannerId)}
        alt={banner.title}
        draggable={false}
      />
      {banner.content && (
        <div className="hero-banner-caption" dangerouslySetInnerHTML={{ __html: banner.content }} />
      )}
    </div>
  );

  if (!banner.linkUrl) return content;

  const isBlank = banner.linkTarget === "_blank";

  if (banner.linkUrl.startsWith("/")) {
    return isBlank ? (
      <Link to={banner.linkUrl} target="_blank">{content}</Link>
    ) : (
      <Link to={banner.linkUrl}>{content}</Link>
    );
  }

  return isBlank ? (
    <a href={banner.linkUrl} target="_blank" rel="noreferrer">{content}</a>
  ) : (
    <a href={banner.linkUrl} target="_self">{content}</a>
  );
}

/** 배너 개수 기준으로 index 를 안전하게 순환시킨다 (음수/초과 인덱스 모두 보정). */
function wrapIndex(i, length) {
  return ((i % length) + length) % length;
}

function BannerSlider({ banners }) {
  const [index, setIndex] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  // 처음<->마지막 배너 사이를 드래그로 넘어갈 때(순환), 트랙이 전체 배너를 가로질러
  // "긴 방향으로" 슬라이드하는 것처럼 보이지 않도록 그 순간만 transition 을 잠깐 끈다.
  const [suppressTransition, setSuppressTransition] = useState(false);

  const viewportRef = useRef(null);
  const draggingRef = useRef(false);
  const startXRef = useRef(0);
  const dragOffsetRef = useRef(0);
  // 화면에 보이는 것은 저항감 때문에 실제보다 덜 움직인 값(dragOffsetRef)이지만,
  // "넘어갔는지" 판정은 항상 실제 손가락/마우스 이동거리(rawDeltaRef) 기준으로 해야
  // 경계(첫/마지막 배너)에서 유독 더 멀리 끌어야만 넘어가는 것처럼 느껴지지 않는다.
  const rawDeltaRef = useRef(0);
  const movedRef = useRef(false);
  const capturedRef = useRef(false);

  const parallaxOffset = useParallaxOffset(viewportRef);

  useEffect(() => {
    if (banners.length <= 1) return;
    const timer = setInterval(() => {
      if (draggingRef.current) return;
      setIndex((i) => (i + 1) % banners.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [banners.length]);

  // 순환(wrap) 이동으로 인해 transition 을 껐다면, 그 프레임이 반영된 직후 다시 켜서
  // 이후의 자동 롤링/인디케이터 클릭 전환은 계속 부드럽게 애니메이션되게 한다.
  useEffect(() => {
    if (!suppressTransition) return undefined;
    const id = window.requestAnimationFrame(() => setSuppressTransition(false));
    return () => window.cancelAnimationFrame(id);
  }, [suppressTransition, index]);

  const handlePointerDown = (e) => {
    if (banners.length <= 1 || (e.pointerType === "mouse" && e.button !== 0)) return;
    draggingRef.current = true;
    movedRef.current = false;
    capturedRef.current = false;
    rawDeltaRef.current = 0;
    startXRef.current = e.clientX;
    setIsDragging(true);
    // 주의: 여기서 곧바로 setPointerCapture 를 걸면 안 된다. 캡처를 걸면 그 이후의
    // 클릭(포인터업) 이벤트까지 실제 <a>/<Link> 가 아니라 이 div 로 리타겟팅되어,
    // 드래그 없이 그냥 클릭만 해도 배너 링크 이동이 아예 동작하지 않게 된다.
    // 그래서 캡처는 실제로 드래그(5px 이상 이동)가 확인된 순간(handlePointerMove)에만 건다.
  };

  const handlePointerMove = (e) => {
    if (!draggingRef.current) return;
    const rawDelta = e.clientX - startXRef.current;
    rawDeltaRef.current = rawDelta;

    if (Math.abs(rawDelta) > 5) {
      movedRef.current = true;
      if (!capturedRef.current) {
        capturedRef.current = true;
        e.currentTarget.setPointerCapture(e.pointerId);
      }
    }

    // 화면에 보여줄 값에만 저항감을 적용한다 (첫 배너에서 더 이전으로, 마지막 배너에서
    // 더 다음으로 끄는 경우) - 빈 공간이 갑자기 드러나는 대신 "끝에 닿았다"는 느낌을 준다.
    let visualDelta = rawDelta;
    const atFirst = index === 0;
    const atLast = index === banners.length - 1;
    if ((atFirst && visualDelta > 0) || (atLast && visualDelta < 0)) {
      visualDelta *= 0.35;
    }

    dragOffsetRef.current = visualDelta;
    setDragOffset(visualDelta);
  };

  const endDrag = () => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    setIsDragging(false);

    const width = viewportRef.current?.offsetWidth || window.innerWidth || 1;
    const threshold = width * 0.15;
    // 저항감이 적용된 화면상의 이동량이 아니라, 실제 끈 거리 기준으로 넘어갈지 판단한다.
    // 그래야 경계(첫/마지막 배너)에서도 다른 배너와 똑같은 거리만 끌면 넘어간다.
    const rawDelta = rawDeltaRef.current;

    if (banners.length > 1 && Math.abs(rawDelta) >= threshold) {
      const goingNext = rawDelta < 0; // 왼쪽으로 끌면 다음, 오른쪽으로 끌면 이전 배너
      const isWrap = goingNext ? index === banners.length - 1 : index === 0;
      if (isWrap) setSuppressTransition(true);
      setIndex(wrapIndex(index + (goingNext ? 1 : -1), banners.length));
    }

    rawDeltaRef.current = 0;
    dragOffsetRef.current = 0;
    setDragOffset(0);
  };

  /** 드래그로 슬라이드를 넘긴 직후 발생하는 클릭(=링크 이동)을 한 번만 무시한다. */
  const handleClickCapture = (e) => {
    if (movedRef.current) {
      e.preventDefault();
      e.stopPropagation();
      movedRef.current = false;
    }
  };

  if (banners.length === 0) return null;

  return (
    <div className="hero-banner-wrap">
      <div
        className={`hero-banner-viewport${isDragging ? " dragging" : ""}`}
        ref={viewportRef}
        /*
          onPointerLeave 로도 드래그를 끝내면, 시작 지점이 배너 가장자리에 가까울 때
          (특히 왼쪽으로 끌 때) 실제 이동거리가 threshold 에 못 미친 상태에서 커서/손가락이
          배너 영역 밖으로 살짝만 나가도 드래그가 조기 종료되어 버렸다 - setPointerCapture 를
          쓰면 pointerup/pointercancel 은 요소 밖에서도 정상적으로 계속 잡히므로, 드래그 종료는
          그 둘만으로 판단한다 (pointerleave 는 실제 위치 기준이라 캡처와 무관하게 발생한다).
        */
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onClickCapture={handleClickCapture}
      >
        <div
          className="hero-banner-track"
          style={{
            transform: `translateX(calc(-${index * 100}% + ${dragOffset}px))`,
            transition: isDragging || suppressTransition ? "none" : undefined,
          }}
        >
          {banners.map((banner) => (
            <div className="hero-banner-slide" key={banner.bannerId}>
              <BannerSlide banner={banner} parallaxOffset={parallaxOffset} />
            </div>
          ))}
        </div>
      </div>

      {banners.length > 1 && (
        <div className="hero-banner-dots">
          {banners.map((b, i) => (
            <button
              key={b.bannerId}
              type="button"
              className={`hero-dot${i === index ? " active" : ""}`}
              onClick={() => setIndex(i)}
              aria-label={`배너 ${i + 1}번으로 이동`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function NoticeList({ notices }) {
  const [openId, setOpenId] = useState(null);

  if (notices.length === 0) {
    return <p className="text-sub">등록된 공지사항이 없습니다.</p>;
  }

  return (
    <div className="notice-list">
      {notices.map((n) => (
        <div className="notice-item" key={n.noticeId}>
          <button type="button" className="notice-item-head" onClick={() => setOpenId(openId === n.noticeId ? null : n.noticeId)}>
            {n.fixTopYn === "Y" && <span className="status-tag" style={{ marginRight: 8 }}>고정</span>}
            <span className="notice-title">{n.title}</span>
            <span className="notice-date">{String(n.createdAt).slice(0, 10)}</span>
          </button>
          {openId === n.noticeId && (
            <div className="notice-item-content" dangerouslySetInnerHTML={{ __html: n.content }} />
          )}
        </div>
      ))}
    </div>
  );
}

export default function HomePage() {
  const [banners, setBanners] = useState([]);
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getBanners(), getNotices(8)])
      .then(([bannerRes, noticeRes]) => {
        setBanners(bannerRes.data || []);
        setNotices(noticeRes.data || []);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="page home-page">
      {!loading && <BannerSlider banners={banners} />}

      <div className="home-section">
        <div className="home-section-head">
          <h2>공지사항</h2>
          <Link to="/products" className="link-btn">상품 보러가기 →</Link>
        </div>
        {loading ? <p className="text-sub">불러오는 중...</p> : <NoticeList notices={notices} />}
      </div>
    </div>
  );
}
